import type { AxiosError } from "axios";
import type { ApiErrorResponseDto } from "@/src/types/api.types";

export class ApiClientError extends Error {
  readonly statusCode: number;
  readonly payload?: ApiErrorResponseDto;

  constructor(message: string, statusCode: number, payload?: ApiErrorResponseDto) {
    super(message);
    this.name = "ApiClientError";
    this.statusCode = statusCode;
    this.payload = payload;
  }
}

export function parseAxiosError(error: AxiosError<ApiErrorResponseDto>): ApiClientError {
  const statusCode = error.response?.status ?? error.status ?? 500;
  const payload = error.response?.data;
  const fieldMessages =
    payload?.errors && typeof payload.errors === "object"
      ? Object.values(payload.errors).flat().join(", ")
      : undefined;

  const message =
    payload?.message ??
    fieldMessages ??
    error.message ??
    "Request failed";

  return new ApiClientError(message, statusCode, payload);
}

export function getApiErrorTitle(error: unknown, fallbackTitle: string): string {
  if (error instanceof ApiClientError) {
    const title = error.payload?.title?.trim();
    if (title) return title;
  }

  if (error && typeof error === "object" && "payload" in error) {
    const payload = (error as { payload?: ApiErrorResponseDto }).payload;
    const title = payload?.title?.trim();
    if (title) return title;
  }

  return fallbackTitle;
}

export function isTimeoutError(error: AxiosError): boolean {
  return error.code === "ECONNABORTED";
}

export function isDeactivatedAccountError(
  httpStatus: number | undefined,
  payload: ApiErrorResponseDto | undefined,
): boolean {
  if (httpStatus !== 403 || !payload) {
    return false;
  }

  const fields = [payload.message, payload.errorCode, payload.error].filter(
    (value): value is string => typeof value === "string" && value.length > 0,
  );

  return fields.some((value) => value.toLowerCase().includes("deactivated"));
}
